import { logger } from '@/shared/utils';
import storage from '@/shared/utils/storage';
import { i18n } from '@/localization/i18n';

export const GROUP_USER_ADDED = 'userAdded';
export const GROUP_USER_REMOVED = 'userRemoved';
export const GROUP_USER_MADE_ADMIN = 'madeAdmin';
export const GROUP_USER_REMOVED_FROM_ADMIN = 'removedFromAdmin';
export const GROUP_USER_LEFT = 'userLeft';
export const GROUP_PROFILE_INFO_UPDATED = 'profileUpdated';
export const GROUP_CHAT_PROFILE_UPDATED_NOTIFY = 'groupProfileUpdated';
export const GROUP_CREATED = 'groupCreated';

const isLocalUser = userId => {
   const currentUser = storage.getItem('username');
   return userId === currentUser;
};

/**
 * Generates appropriate notification message for group status changes
 * @param {Object} options - Notification options
 * @param {string} options.publisherId - ID of the user who performed the action
 * @param {string} options.toUserId - ID of the user affected by the action
 * @param {string} options.status - The group status change type
 * @param {string} [options.publisher=''] - Display name of the publisher
 * @param {string} [options.toUser=''] - Display name of the affected user
 * @returns {string} Formatted notification message
 */
export const groupNotifyStatus = ({ publisherId, toUserId, status, publisher = '', toUser = '' }) => {
   try {
      const publisherName = publisher || publisherId;
      const toUserName = toUser || toUserId;
      const groupName = 'this group';
      const isPublisherLocalUser = isLocalUser(publisherId);
      const isToUserLocalUser = isLocalUser(toUserId);

      // Message templates for different status types
      const messageTemplates = {
         [GROUP_USER_REMOVED_FROM_ADMIN]: () =>
            isToUserLocalUser
               ? i18n.t('group.REMOVED_FROM_ADMIN_YOU')
               : i18n.t('group.REMOVED_FROM_ADMIN_OTHER', { name: toUserName }),
         [GROUP_CREATED]: () =>
            isPublisherLocalUser
               ? i18n.t('group.CREATED_YOU', { groupName })
               : i18n.t('group.CREATED_OTHER', { name: publisherName, groupName }),
         [GROUP_USER_ADDED]: () => {
            if (publisherId === toUserId) {
               if (isPublisherLocalUser) return i18n.t('group.JOINED_YOU');
               return i18n.t('group.JOINED_OTHER', { name: publisherName });
            }
            if (isToUserLocalUser) {
               if (isPublisherLocalUser) return '';
               return i18n.t('group.ADDED_YOU_BY_OTHER', { name: publisherName });
            }
            if (isPublisherLocalUser) return i18n.t('group.ADDED_YOU', { name: toUserName });
            return i18n.t('group.ADDED_OTHER', { name: publisherName, name2: toUserName });
         },
         [GROUP_USER_REMOVED]: () => {
            if (isToUserLocalUser) {
               if (isPublisherLocalUser) return i18n.t('group.LEFT_YOU');
               return i18n.t('group.REMOVED_YOU', { name: publisherName });
            }
            if (isPublisherLocalUser) return i18n.t('group.REMOVED_BY_YOU', { name: toUserName });
            return i18n.t('group.REMOVED_OTHER', { name: publisherName, name2: toUserName });
         },
         [GROUP_USER_LEFT]: () => {
            if (isToUserLocalUser) {
               if (isPublisherLocalUser) return i18n.t('group.LEFT_YOU');
               return i18n.t('group.REMOVED_YOU', { name: publisherName });
            }
            if (toUserId === publisherId) {
               return i18n.t('group.LEFT_OTHER', { name: publisherName });
            }
            if (isPublisherLocalUser) return i18n.t('group.REMOVED_BY_YOU', { name: toUserName });
            return i18n.t('group.REMOVED_OTHER', { name: publisherName, name2: toUserName });
         },
         [GROUP_PROFILE_INFO_UPDATED]: () =>
            isPublisherLocalUser
               ? i18n.t('group.PROFILE_UPDATED_YOU', { groupName })
               : i18n.t('group.PROFILE_UPDATED_OTHER', { name: publisherName, groupName }),
         [GROUP_CHAT_PROFILE_UPDATED_NOTIFY]: () =>
            isPublisherLocalUser
               ? i18n.t('group.PROFILE_UPDATED_YOU', { groupName })
               : i18n.t('group.PROFILE_UPDATED_OTHER', { name: publisherName, groupName }),
         [GROUP_USER_MADE_ADMIN]: () =>
            isToUserLocalUser ? i18n.t('group.MADE_ADMIN_YOU') : i18n.t('group.MADE_ADMIN_OTHER', { name: toUserName }),
      };

      const resolvedStatus = status || GROUP_PROFILE_INFO_UPDATED;
      const templateFunction = messageTemplates[resolvedStatus];
      if (!templateFunction) {
         logger.warn(`Unknown group status: ${status}`);
         return '';
      }

      return templateFunction();
   } catch (error) {
      logger.error('Failed to construct notification', error);
      return '';
   }
};
